import React, {useContext, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {
    Button,
    Check,
    Grid,
    GridColumn,
    Modal,
    ModalCloser,
    ModalContent,
    ModalFooter,
    ModalHeader,
    Select,
    TextField,
    TMultipleSelectValue,
} from '../../../patterns';
import {StoreContext} from '../base/root-store';

export const AddAndEditEmployeeModal = observer((): JSX.Element => {
    const store = useContext(StoreContext).companyStore;
    const userStore = useContext(StoreContext).userStore;
    const initialEmployee = store.variables.employeeInEditing;
    const defaultBusinessUnitId = userStore.payload?.isAdmin === true ? store.payload!.rootOneBusinessUnit.id : userStore.payload!.businessUnitId;
    const tagsOptions = store.payload!.allTags !== undefined ? store.payload!.allTags.map((it) => ({
        label: it,
        value: it,
    })).sort() : [];

    const [firstName, setFirstName] = useState<string>(initialEmployee ? initialEmployee.employeeFirstName : '');
    const [lastName, setLastName] = useState<string>(initialEmployee ? initialEmployee.employeeLastName : '');
    const [email, setEmail] = useState<string>(initialEmployee && initialEmployee.email ? initialEmployee.email : '');
    const [phoneNumber, setPhoneNumber] = useState<string>(initialEmployee && initialEmployee.phoneNumber ? initialEmployee.phoneNumber : '');
    const [businessUnitId, setBusinessUnitId] = useState<string>(initialEmployee ? initialEmployee.businessUnitId : defaultBusinessUnitId);
    const [position, setPosition] = useState<string>(initialEmployee ? initialEmployee.position : '');
    const [locationId, setLocationId] = useState<string>(initialEmployee ? initialEmployee.locationId : store.payload!.allLocations[0]);
    const [startDate, setStartDate] = useState<string>(initialEmployee ? initialEmployee.startDate : '');
    const [isManager, setIsManager] = useState<boolean>(initialEmployee ? initialEmployee.isManager === true : false);
    const [isAdmin, setIsAdmin] = useState<boolean>(initialEmployee ? initialEmployee.isAdmin === true : false);
    const [tag, setTag] = useState<TMultipleSelectValue | undefined>(initialEmployee !== undefined && initialEmployee.employeeTags !== undefined && initialEmployee.employeeTags.length > 0 ? initialEmployee!.employeeTags : ['']);
    const [newTag, setNewTag] = useState<string>('');
    const [businessUnitsToManage, setBusinessUnitsToManage] = useState<TMultipleSelectValue | undefined>(initialEmployee?.managedBusinessUnitIds);

    const showInvalidEmail = store.variables.phoneAndEmailBothMissing || email !== '';
    const [isOpened, setIsOpened] = useState<boolean>(false);

    const renderAddFilterValueButton = <Button
        text={store.label('company.employee-modal.create-tag-button')}
        color={'white'}
        iconLeft={isOpened ? 'minus' : 'plus'}
        onClick={(event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>): void => {
            event.preventDefault();
            if (isOpened) {
                setIsOpened(false);
            } else {
                setIsOpened(true);
            }
        }}
    />;

    return (
        <Modal size="small">
            <ModalHeader>
                <h4>{store.variables.openModal=== 'add-employee' ? store.label('company.employee-modal.title-add') : store.label('company.employee-modal.title-edit')}</h4>
            </ModalHeader>
            <ModalContent>
                <div className="text">
                    <TextField
                        label={store.label('company.employee-modal.first-name')}
                        defaultValue={firstName}
                        onChange={((value): void => setFirstName(value))}
                        id='add-employee-firstname'
                        name='add-employee-firstname'
                        description={store.variables.showValidation && store.getNameError(firstName, 'emp_firstname_forbidden') !== undefined ? store.label(`common.validation.${store.getNameError(firstName, 'emp_firstname_forbidden')}`) : undefined}
                        invalid={store.variables.showValidation && store.getNameError(firstName, 'emp_firstname_forbidden') !== undefined}
                    />
                    <TextField
                        label={store.label('company.employee-modal.last-name')}
                        defaultValue={lastName}
                        onChange={((value): void => setLastName(value))}
                        id='add-employee-lastname'
                        name='add-employee-lastname'
                        description={store.variables.showValidation && store.getNameError(lastName, 'emp_lastname_forbidden') !== undefined ? store.label(`common.validation.${store.getNameError(lastName, 'emp_lastname_forbidden')}`) : undefined}
                        invalid={store.variables.showValidation && store.getNameError(lastName, 'emp_lastname_forbidden') !== undefined}
                    />
                    <TextField
                        label={store.label('company.employee-modal.email')}
                        defaultValue={email}
                        onChange={((value): void => setEmail(value))}
                        id='add-employee-email'
                        name='add-employee-email'
                        description={store.variables.showValidation && showInvalidEmail && store.checkEmail(email) !== undefined ? store.label(`common.validation.${store.checkEmail(email)}`) : undefined}
                        invalid={store.variables.showValidation && showInvalidEmail && store.checkEmail(email) !== undefined}
                    />
                    <p className="textfield__text-outside-fields">
                        {store.label('company.employee-modal.or')}
                    </p>
                    <TextField
                        className="textfield__no-top-margin"
                        label={store.label('company.employee-modal.phone')}
                        defaultValue={phoneNumber}
                        onChange={((value): void => setPhoneNumber(value))}
                        id='add-employee-phoneNumber'
                        name='add-employee-phoneNumber'
                        description={store.variables.showValidation && phoneNumber !== '' && store.checkPhoneNumber(phoneNumber) !== undefined ? store.label(`common.validation.${store.checkPhoneNumber(phoneNumber)}`) : undefined}
                        invalid={store.variables.showValidation && phoneNumber !== '' && store.checkPhoneNumber(phoneNumber) !== undefined}
                    />
                    <TextField
                        label={store.label('company.employee-modal.start-date')}
                        focusLabel={store.label('company.employee-modal.start-date-format')}
                        defaultValue={startDate}
                        onChange={(value): void => setStartDate(value)}
                        id='add-employee-modal.start-date'
                        name='add-employee-modal.start-date'
                        description={store.variables.showValidation && startDate !== '' && store.checkStartDate(startDate) !== undefined ? store.label(`common.validation.${store.checkStartDate(startDate)}`) : undefined}
                        invalid={store.variables.showValidation && startDate !== '' && store.checkStartDate(startDate) !== undefined}
                    />
                    <Select
                        label={store.label('company.employee-modal.business-unit')}
                        defaultValue={businessUnitId}
                        onChange={(value): void => setBusinessUnitId('' + value)}
                        options={userStore.payload!.businessUnits.map((businessUnit) => ({
                            label: businessUnit.name,
                            value: businessUnit.id,
                        }))}
                        search={{
                            label: store.label('company.employee-modal.business-unit.search'),
                            id: 'add-employee-department-search',
                            name: 'add-employee-department-search',
                        }}
                        id='add-employee-department'
                        name='add-employee-department'
                        type={undefined}
                    />
                    <TextField
                        label={store.label('company.employee-modal.position')}
                        defaultValue={position}
                        onChange={((value): void => setPosition(value))}
                        id='add-employee-position'
                        name='add-employee-position'
                    />
                    <Select
                        label={store.label('company.employee-modal.location')}
                        defaultValue={locationId}
                        onChange={(value): void => setLocationId('' + value)}
                        options={store.payload!.allLocations.map((location) => ({
                            label: store.label(`common.location.${location}`),
                            value: location,
                        }),
                        )
                        }
                        search={{
                            label: store.label('company.employee-modal.location-search'),
                            id: 'add-employee-location-search',
                            name: 'add-employee-location-search',
                        }}
                        id='add-employee-location'
                        name='add-employee-location'
                        type={undefined}
                    />
                    <Select
                        label={store.label('company.employee-modal.select-tags')}
                        defaultValue={tag}
                        onChange={(value): void => setTag(value)}
                        options={[
                            {
                                label: store.label('company.employee-modal.no-values'),
                                value: '',
                            },
                            ...tagsOptions,
                        ]}
                        search={{
                            label: store.label('company.employee-modal.search-tags'),
                            id: 'search-tags',
                            name: 'search-tags',
                        }}
                        id='select-tags'
                        name='select-tags'
                        type={'multiple'}
                    />
                    <div className={'modal__custom-button'}>
                        {renderAddFilterValueButton}
                    </div>
                    {isOpened &&
                        <TextField
                            label={store.label('company.employee-modal.add-tag')}
                            defaultValue={newTag}
                            onChange={((value): void => setNewTag(value))}
                            id='add-tag'
                            name='add-tag'
                            description={store.variables.showValidation && store.getTagNameError('tag_forbidden', 'duplicate_tag') !== undefined ? store.label(`common.validation.${store.getTagNameError('tag_forbidden', 'duplicate_tag')}`) : undefined}
                            invalid={store.variables.showValidation && store.getTagNameError('tag_forbidden', 'duplicate_tag') !== undefined}
                        />
                    }
                    <Check
                        label={store.label('company.employee-modal.is-manager')}
                        defaultChecked={isManager}
                        onChange={(checked): void => setIsManager(checked)}
                        id='is-manager'
                        name='is-manager'
                        value='is-manager'/>
                    {userStore.payload?.isAdmin &&
                        <Check
                            label={store.label('company.employee-modal.is-admin')}
                            defaultChecked={isAdmin}
                            onChange={(checked): void => setIsAdmin(checked)}
                            id='is-admin'
                            name='is-admin'
                            value='is-admin'
                            disabled={initialEmployee?.isLastAdmin}/>
                    }
                </div>
                <br/>
                <Select
                    label={store.label('menu.partner-managed-units')}
                    defaultValue={businessUnitsToManage}
                    onChange={(value): void => setBusinessUnitsToManage(value)}
                    options={[
                        ...userStore.payload!.businessUnits.map((businessUnit) => ({
                            label: businessUnit.name,
                            value: businessUnit.id,
                        })).sort((a,b) => a.label.localeCompare(b.label))]}
                    search={{
                        label: store.label('company.employee-modal.business-unit.search'),
                        id: 'add-manager-right-employee-name',
                        name: 'add-manager-right-employee-name',
                    }}
                    id="add-manager-right-department-name"
                    name="add-manager-right-department-name"
                    type="multiple"
                />
            </ModalContent>
            <ModalFooter>
                <Grid wrap={['xs']} justify={['between-xs']}>
                    <GridColumn width={['xs-12', 'sm-6']}>
                        <ModalCloser>
                            <Button block={true} color="white" text={store.label('company.modal.cancel')}/>
                        </ModalCloser>
                    </GridColumn>
                    <GridColumn width={['xs-12', 'sm-6']}>

                        <Button block={true} text={store.label('company.modal.save')} onClick={(): void => store.saveEmployee({
                            firstName,
                            lastName,
                            email: email !== '' ? email : undefined,
                            phoneNumber: phoneNumber !== '' ? phoneNumber : undefined,
                            businessUnitId,
                            position: position !== '' ? position : undefined,
                            locationId,
                            startDate: startDate !== '' ? startDate : undefined,
                            isManager,
                            isAdmin,
                            employeeTags: tag?.length === 0 ? [''] : tag,
                            newTag: newTag === '' ? undefined : newTag,
                            businessUnitsToManage,
                        })}/>

                    </GridColumn>
                </Grid>
            </ModalFooter>
        </Modal>
    );
});
